import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';

import { reportError } from '../reportError.ts';
import type { Snippet } from '../snippetTypes.ts';

/**
 * Hook for inserting a snippet at the current cursor position in the editor.
 * Pass editorAPI from the caller (e.g. useFullPageEditorApiState or EditorContextPanelContext).
 *
 * @returns A function to insert a snippet, returns true on success, false on failure
 */
export const useInsertSnippet = (editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null) => {
	return (snippet: Snippet): boolean => {
		if (!editorAPI?.core?.actions?.execute) {
			return false;
		}

		const body = snippet.body;
		if (!body) {
			return false;
		}

		try {
			editorAPI.core.actions.execute(({ tr }: { tr: any }) => {
				const schema = tr.doc.type.schema;

				// Convert snippet body to ProseMirror content
				// Body can be either a single ADFEntity or an array of ADFEntity
				const content = Array.isArray(body)
					? Fragment.fromJSON(schema, body)
					: schema.nodeFromJSON(body);

				// Insert content at current selection using safeInsert
				// - position: undefined = use current selection position
				// - tryToReplace: true = replace selection if something is selected
				safeInsert(content, undefined, true)(tr);

				return tr;
			});

			return true;
		} catch (error) {
			reportError(error);
			return false;
		}
	};
};
